import { ApiResponse, FormStore, GlobalStore } from '@roc/feature-app-core';
import { AppraisalsService } from '@roc/feature-appraisals';
import {
  DateFormat,
  DescribedRennovation,
  floridaCountiesRestriction,
  formatDate,
  generateUUID,
  GENERIC_ERROR_MESSAGE,
  NEW_CONSTRUCTION,
  notLicensedinState,
  parseAddress,
  propertyCitiesRestriction,
  PropertyOwnership,
  PURCHASE,
  REFINANCE,
  statePropertyTypeRestriction,
} from '@roc/feature-utils';
import { isNil } from '@roc/ui/utils';
import { MSAService } from 'libs/feature-loans/src/loanSubmission/services/msaService';
import {
  action,
  computed,
  flow,
  makeObservable,
  observable,
  override,
} from 'mobx';
import { LoanSubType, PROPERTY_TYPE_OTHER, PropertySource, propertySourceValues, propertyTypeOptions } from '../../utils/constants';
import { booleanToYn, getUnitsByPropertyType, isRestrictedCounty, ynToBoolean } from '../../utils/utils';
import { QuoteService } from '../../services/quoteService';

const REQUIRED_MESSAGE = 'This field is required';

const propertyInformationForm = {
  fields: {
    propertyId: {
      value: '',
      error: null,
      rule: '',
      message: '',
    },
    address: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The address is required.',
    },
    __isAddressInCorrectFormat__: {
      value: true,
      error: null,
      rule: 'accepted',
      message: 'Invalid Address Format.',
    },
    streetNumber: {
      value: '',
      error: null,
      rule: '',
    },
    streetName: {
      value: '',
      error: null,
      //rule: 'required',
      message: 'The Street Name is required.',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The City is required.',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The State is required.',
    },
    county: {
      value: '',
      error: null,
      rule: '',
    },
    zipCode: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The Zip Code is required.',
    },
    latitude: {
      value: 0,
      error: null,
      rule: '',
    },
    longitude: {
      value: 0,
      error: null,
      rule: '',
    },
    aptNumber: {
      value: '',
      error: null,
      rule: '',
    },
    propertyType: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The property type is required.',
    },
    propertyTypeDescription: {
      value: '',
      error: null,
      rule: [{ required_if: ['propertyType', PROPERTY_TYPE_OTHER] }],
      message: 'This field is required.',
    },
    units: {
      value: '',
      error: null,
      rule: '',
      message: 'This field is required.',
    },
    loanPurpose: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    seekingCashOut: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['outstandingLoanOnTheProperty', true],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    purchaseDate: {
      type: 'DATE',
      value: null,
      error: null,
      rule: [
        {
          required_if_one_of: ['loanPurpose', REFINANCE],
        },
        { valid_date: true },
      ],
      format: 'MM/dd/yyyy',
      message: 'Purchase Date is required.',
    },
    propertySourcing: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['propertyOwnership', PURCHASE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    sellerCredit: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['propertyOwnership', PURCHASE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    sellerCreditAmount: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['sellerCredit', true],
        },
      ],
      message: 'The Seller Credit Amount is required.',
    },
    borrowerKnowOwnerOfRecord: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['propertyOwnership', PURCHASE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    armsLengthComment: {
      value: '',
      error: null,
      rule: [
        {
          required_if: ['borrowerKnowOwnerOfRecord', true],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    outstandingLoanOnTheProperty: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['loanPurpose', REFINANCE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    payoffAmount: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['outstandingLoanOnTheProperty', true],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    monthlyMarketRent: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    annualTaxes: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    annualInsurance: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    purchasePrice: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    currentMarketValue: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    annualHOAProvidedAtOrigination: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_MESSAGE,
    },
    constructionRenoSpendToDate: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['loanPurpose', REFINANCE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    squareFootageExpansion: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: ['propertyOwnership', PURCHASE],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    wholesalerPropertyContractAmount: {
      value: '',
      error: null,
      rule: [
        {
          required_if_one_of: [
            'propertySourcing',
            propertySourceValues.wholesaler,
          ],
        },
      ],
      message: REQUIRED_MESSAGE,
    },
    propertySourcingExplanation: {
      value: '',
      error: null,
      rule: [
        { required_if_one_of: ['propertySourcing', PropertySource.OTHER] },
      ],
      message: REQUIRED_MESSAGE,
    },
    //Supplemental Information
    hasNewAddress: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    exitStrategy: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    loanEverBeenInDefault: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_MESSAGE,
    },
    totalAnnualNOI: {
      value: '',
      error: null,
      rule: '',
    },
    totalAnnualIncome: {
      value: '',
      error: null,
      rule: '',
    },
    isPropertyLeased: {
      value: 'Y',
      error: null,
      rule: [
        {
          required_if_one_of: ['propertyOwnership', PURCHASE],
        },
      ],
    },
    section8: {
      value: '',
      error: null,
      rule: 'required',
    },
    //Flags
    isAppraisalAvailable: { value: false },
    isPreflight: { value: false },
    //Term Sheet Fields
    releaseAmount: { value: null },
    propertyLoanAllocatedAmount: { value: null },
  },
  meta: {
    key: null,
    editMode: false,
    isValid: false,
    error: null,
  },
};

export class SinglePropertyPropertiesStore {
  protected globalStore: GlobalStore;
  protected loanSubType: string;
  private quoteService: QuoteService;

  allCounties: any[];

  public propertyFormStores: SinglePropertyPropertyFormStore[];
  public key = generateUUID();

  constructor(globalStore, loanSubType) {
    this.globalStore = globalStore;
    this.loanSubType = loanSubType;
    this.quoteService = new QuoteService();
    makeObservable(this, {
      reset: action,
      propertyFormStores: observable,
      createPropertyForm: action,
      addNewProperty: action,
      removeProperty: action,
      validateAllProperties: action,
      turnOffPropertiesEditMode: action,
      getProperties: action,
      loadProperties: action,
      allCounties: observable,
      fetchStateCounties: flow,
      counties: computed,
    });
  }

  createPropertyForm() {
    return new SinglePropertyPropertyFormStore(
      propertyInformationForm,
      this.globalStore,
      this.loanSubType
    );
  }

  reset() {
    this.propertyFormStores = [];
    this.allCounties = [];
    this.addNewProperty();
  }

  addNewProperty() {
    this.propertyFormStores.push(this.createPropertyForm());
  }

  removeProperty(propertyKey: string) {
    this.propertyFormStores = this.propertyFormStores.filter(
      property => property.key !== propertyKey
    );
  }

  validateAllProperties() {
    let isValid = true;

    this.propertyFormStores.forEach(store => {
      const state = store.form.fields.state.value;

      store.validateAndCleanupProperty();
      isValid = isValid && store.form.meta.isValid;
      store.setEditMode(!isValid);
    });

    if (!isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please complete all the required fields for the properties.',
      });
      return false;
    }

    return true;
  }

  turnOffPropertiesEditMode() {
    this.propertyFormStores.forEach(propertyFormStore => {
      propertyFormStore.form.meta.editMode = false;
    });
  }

  getProperties() {
    return this.propertyFormStores.map(form => form.getProperty());
  }

  loadProperties(quoteData) {
    const properties = quoteData.properties;
    if (properties?.length > 0) {
      this.propertyFormStores = properties?.map(property => {
        const form = this.createPropertyForm();
        form.loadProperty(property);
        form.setEditMode(false);
        return form;
      });
    }
  }

  *fetchStateCounties() {
    try {
      const response: ApiResponse = yield this.quoteService.getStateCounties();
      this.allCounties = response.data;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting state counties.',
      });
    }
  }

  get counties() {
    const propertyFormFields =
      this.propertyFormStores.length > 0
        ? this.propertyFormStores[0].form.fields
        : '';
    const states = propertyFormFields.state.value
      ? [propertyFormFields.state.value]
      : [];
    let selectedStateCounties = [];
    if (states && states.length > 0) {
      selectedStateCounties = states.reduce(
        (c, s) => [...c, ...(this.allCounties[s] ?? [])],
        []
      );
    }

    const counties = [];
    if (selectedStateCounties && selectedStateCounties.length > 0) {
      selectedStateCounties.sort().map(county => {
        const countyParts = county.split(',');
        counties.push({ value: countyParts[0].trim(), label: county });
      });
    }
    return counties;
  }
}

const RenoDictionary = {
  NO: DescribedRennovation.NO,
  HORIZONTAL: DescribedRennovation.HORIZONTAL,
  VERTICAL: DescribedRennovation.VERTICAL,
  VERTICAL_AND_HORIZONTAL: DescribedRennovation.VERTICAL_AND_HORIZONTAL,
  [DescribedRennovation.CONDO_CONVERSION]:
    DescribedRennovation.CONDO_CONVERSION,
  [DescribedRennovation.UNIT_COUNT]: DescribedRennovation.UNIT_COUNT,
  [DescribedRennovation.ADU_CONVERSION]: DescribedRennovation.ADU_CONVERSION,
  [DescribedRennovation.FIRE_DAMAGE]: DescribedRennovation.FIRE_DAMAGE,
  [DescribedRennovation.CONVERSION_EXISTING_SPACE]:
    DescribedRennovation.CONVERSION_EXISTING_SPACE,
};

export class SinglePropertyPropertyFormStore extends FormStore {
  private globalStore: GlobalStore;
  private appraisalService: AppraisalsService;
  public loanSubType: string;
  private msaService = new MSAService();

  key = generateUUID();
  isEditing = true;

  constructor(form, globalStore, loanSubType) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.loanSubType = loanSubType;
    this.appraisalService = new AppraisalsService();
    this.prepopulateNewProperty();

    makeObservable(this, {
      key: observable,
      isEditing: observable,
      reset: override,
    });
  }

  reset() {
    super.reset();
    this.isEditing = true;
    this.form.meta.editMode = true;
  }

  prepopulateNewProperty() {
    this.form.meta.editMode = true;
  }
  setEditMode(value) {
    this.form.meta.editMode = value;
    this.isEditing = value;
  }
  saveAddressField(address: string, addressDetails, geometryDetails) {
    const parsedAddress = parseAddress(
      address,
      addressDetails,
      geometryDetails
    );

    const streetAddress =
      parsedAddress.street_number === null
        ? parsedAddress.street
        : parsedAddress.street_number + ' ' + parsedAddress.street;
    this.onFieldChange('address', streetAddress);
    this.onFieldChange('streetNumber', parsedAddress.street_number);
    this.onFieldChange('streetName', parsedAddress.street);
    this.onFieldChange('city', parsedAddress.city);
    this.onFieldChange('state', parsedAddress.state);
    this.onFieldChange('county', parsedAddress.county);
    this.onFieldChange('zipCode', parsedAddress.zip);
    this.onFieldChange('latitude', parsedAddress.latitude);
    this.onFieldChange('longitude', parsedAddress.longitude);
    this.onFieldChange('aptNumber', parsedAddress.aptNumber);
  }

  /**
   * Validates the property fields and cleans up unused fields (fields hidden because of other fields values)
   */
  validateAndCleanupProperty(message?: string) {
    this.cleanupUnusedFields();
    const state = this.form.fields.state.value;
    const city = this.form.fields.city.value;
    const propertyType = this.form.fields.propertyType.value;
    const county = this.form.fields.county.value?.toLowerCase();
    if (notLicensedinState.states.includes(state)) {
      this.globalStore.notificationStore.showWarningNotification({
        message: notLicensedinState.message,
      });
      return false;
    }

    if (message) {
      this.runFormValidationWithMessage(message);
    } else {
      this.runFormValidation();
    }

    return this.form.meta.isValid;
  }

  getDescribedRenovation(property: any) {
    if (this.loanSubType === LoanSubType.GROUND_UP) {
      return NEW_CONSTRUCTION;
    } else {
      const describedReno =
        property?.describeRenovation &&
          Object.values(RenoDictionary).includes(property.describeRenovation)
          ? property.describeRenovation
          : RenoDictionary[property?.squareFootageExpansion];
      return describedReno || DescribedRennovation.NO;
    }
  }

  /**
   * Cleans up unused fields (fields hidden because of other fields values)
   */
  cleanupUnusedFields() {
    const property = this.getProperty();
    this.reset();
    this.loadProperty(property);
  }

  validateProperty(prefight?: boolean) {
    this.onFieldChange('isPreflight', Boolean(prefight));
    this.runFormValidationWithMessage();
    return this.form.meta.isValid;
  }

  onFieldChange(field, value) {
    super.onFieldChange(field, value);
    if (field == 'loanPurpose') {
      if (value == REFINANCE) {
        super.onFieldChange('propertySourcing', '');
        super.onFieldChange('sellerCredit', '');
        super.onFieldChange('sellerCreditAmount', '');
        super.onFieldChange('borrowerKnowOwnerOfRecord', '');
        super.onFieldChange('armsLengthComment', '');
        super.onFieldChange('wholesalerPropertyContractAmount', '');
        super.onFieldChange('propertySourcingExplanation', '');
        if (this.loanSubType == LoanSubType.GROUND_UP) {
          super.onFieldChange('subdividedIntoLots', '');
          super.onFieldChange('numberOfLots', '');
        } else if (this.loanSubType == LoanSubType.STABILIZED_BRIDGE) {
          super.onFieldChange('squareFootageExpansion', '');
        }
      }
      if (value == PURCHASE) {
        super.onFieldChange('purchaseDate', null);
        super.onFieldChange('outstandingLoanOnTheProperty', '');
        super.onFieldChange('payoffAmount', '');
        super.onFieldChange('loanEverBeenInDefault', '');
        super.onFieldChange('constructionRenoSpendToDate', '');
        if (this.loanSubType == LoanSubType.FIX_AND_FLIP) {
          super.onFieldChange('currentMarketValue', '');
        } else if (this.loanSubType == LoanSubType.GROUND_UP) {
          super.onFieldChange('currentMarketValue', '');
        } else if (this.loanSubType == LoanSubType.STABILIZED_BRIDGE) {
          super.onFieldChange('holdingCosts', '');
        }
      }
    }
  }

  loadProperty(property) {
    const data = {
      propertyId: property.propertyId,
      uuid: property.uuid,
      propertyType: property.propertyType,
      propertyTypeDescription: property.propertyTypeDescription,
      units: property.units,
      loanPurpose: property.loanPurpose,
      propertySourcing: property.propertySourcing,
      borrowerKnowOwnerOfRecord: property.armsLength,
      armsLengthComment: property.armsLengthComment,
      address: property.address?.addressLine1,
      aptNumber: property.address?.addressLine2,
      city: property.address?.city,
      state: property.address?.state,
      county: property.address?.county,
      zipCode: property.address?.zipCode,
      latitude: property.address?.latitude,
      longitude: property.address?.longitude,
      purchaseDate: property.dates?.purchaseDate,
      purchasePrice: property.financials?.purchasePrice,
      currentMarketValue: property.financials?.currentMarketValue,
      renovationBudget: property.financials?.renovationBudget,
      payoffAmount: property.financials?.payoffAmount,
      marketValueAfterCompletion:
        property.financials?.marketValueAfterCompletion,
      monthlyMarketRent: property.financials?.monthlyMarketRent,
      annualTaxes: property.financials?.annualTaxes,
      annualInsurance: property.financials?.annualInsurance,
      annualHOAProvidedAtOrigination: property.financials?.annualHOA,

      currentlyOccupied: property.flags?.currentlyOccupied,
      section8: property.flags?.section8,
      isPropertyLeased: booleanToYn(property.flags?.propertyLeased),
      permitsInPlace: property.flags?.permitsInPlace,
      fireDamage: property.flags?.fireDamage,
      loanEverBeenInDefault: property.flags?.loanEverBeenInDefault,

      describeRenovation: property.renovation?.describeRenovation,
      squareFootageExpansion: property.renovation?.squareFootageExpansion,
      constructionRenoSpendToDate: property.renovation?.capitalImprovements,
      isAppraisalAvailable: property.valuation?.isAppraisalAvailable,
      medianZipCodeValue: property.valuation?.medianZipCodeValue,
      outstandingLoanOnTheProperty: property.outstandingLoanOnTheProperty ?? '',
      sellerCredit: property.sellerCredit ?? '',
      sellerCreditAmount: property.sellerCreditAmount,
      seekingCashOut: property.flags.seekingCashOut ?? '',
      wholesalerPropertyContractAmount: property.financials?.wholesalerPropertyContractAmount ?? '',
    };
    this.loadForm(data);
    this.key = property.uuid;
  }

  getProperty() {
    const values = this.getFormValues();
    const isPurchase = values.loanPurpose === PURCHASE;
    const isRefinance = values.loanPurpose === REFINANCE;
    const describeRenovation =
      this.loanSubType === LoanSubType.GROUND_UP
        ? NEW_CONSTRUCTION
        : values.describeRenovation;
    const isAddingSquareFootage = DescribedRennovation.NO != describeRenovation;
    const isOtherPropertyType = values.propertyType === PROPERTY_TYPE_OTHER;

    return {
      propertyId: values.propertyId,
      uuid: this.key,
      propertyType: values.propertyType,
      propertyTypeDescription: values.propertyTypeDescription,
      units: isOtherPropertyType ? values.units : getUnitsByPropertyType(values.propertyType),
      loanPurpose: values.loanPurpose,
      propertySourcing: values.propertySourcing,
      outstandingLoanOnTheProperty: isRefinance
        ? values.outstandingLoanOnTheProperty
        : null,
      armsLength: isPurchase ? values.borrowerKnowOwnerOfRecord : null,
      armsLengthComment: isPurchase ? values.armsLengthComment : null,
      sellerCredit: isPurchase ? values.sellerCredit : null,
      sellerCreditAmount: isPurchase ? values.sellerCreditAmount : null,

      address: {
        addressLine1: values.address,
        addressLine2: values.aptNumber,
        city: values.city,
        state: values.state,
        county: values.county,
        zipCode: values.zipCode,
        latitude: values.latitude,
        longitude: values.longitude,
      },
      dates: {
        purchaseDate: isRefinance
          ? formatDate(values.purchaseDate, DateFormat.MMDDYYYY)
          : null,
      },
      financials: {
        purchasePrice: values.purchasePrice,
        currentMarketValue: values.currentMarketValue,
        renovationBudget: values.renovationBudget,
        marketValueAfterCompletion: values.marketValueAfterCompletion,
        monthlyMarketRent: values.monthlyMarketRent,
        annualTaxes: values.annualTaxes,
        annualInsurance: values.annualInsurance,
        /*
        monthlyMarketRent: values.monthlyMarketRentProvidedAtOrigination,
        annualTaxes: values.annualTaxesProvidedAtOrigination,
        annualInsurance: values.annualInsuranceProvidedAtOrigination,*/
        payoffAmount: values.payoffAmount,
        annualHOA: values.annualHOAProvidedAtOrigination,
        wholesalerPropertyContractAmount: values.wholesalerPropertyContractAmount,
      },
      flags: {
        currentlyOccupied: values.currentlyOccupied,
        section8: values.section8,
        permitsInPlace: isAddingSquareFootage
          ? values.permitsInPlace
          : undefined,
        fireDamage: describeRenovation
          ? describeRenovation == DescribedRennovation.FIRE_DAMAGE
          : values.fireDamage,
        loanEverBeenInDefault: isRefinance
          ? values.loanEverBeenInDefault
          : null,
        seekingCashOut: values.seekingCashOut,
        propertyLeased: ynToBoolean(values.isPropertyLeased),

      },
      renovation: {
        describeRenovation: describeRenovation,
        squareFootageExpansion: values.squareFootageExpansion,
        capitalImprovements: values.constructionRenoSpendToDate,
      },
      valuation: {
        isAppraisalAvailable: values.isAppraisalAvailable,
        appraisalType: null,
        isSecondAppraisalRequired: null,
        medianZipCodeValue: values.medianZipCodeValue,
      },
    };
  }
}
